import {
  createContext,
  PropsWithChildren,
  use,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLaunchDarkly } from "integrations/launch-darkly/LaunchDarklyContext";
import { FeatureFlags } from "integrations/launch-darkly/featureFlagUtils";
import { logError, logWarning } from "shared/services/ErrorReporting";
import { isHeadlessBrowser, isProduction } from "shared/constants/Environment";
import { GlobalLoading } from "components/loading";
import { useTranslation } from "react-i18next";
import { isRunningInMeticulous } from "~/integrations/meticulous";
import { useAuth } from "./AuthContext";

const clientSideID = import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID;
const isTesting = import.meta.env.TEST;
if (!clientSideID && !isTesting) {
  logError(
    "VITE_LAUNCH_DARKLY_CLIENT_ID is not set, feature flags will not work",
  );
}

interface IFeatureFlagContext {
  features: FeatureFlags;
}

export const FeatureFlagContext = createContext<IFeatureFlagContext>(
  undefined as never,
);

const FLAGS_LOAD_TIMEOUT_MS = 5000;
const FLAGS_LOAD_TIMEOUT_MESSAGE =
  "LaunchDarkly flags failed to load within timeout";

export const reportFlagsTimeout = (): void => {
  const isHeadless = isHeadlessBrowser();
  const reportAsError = isProduction() && !isHeadless;

  const captureContext = {
    level: reportAsError ? ("error" as const) : ("warning" as const),
    tags: { headlessBrowser: isHeadless },
    extra: {
      environment: import.meta.env.VITE_ENVIRONMENT,
      timeoutMs: FLAGS_LOAD_TIMEOUT_MS,
    },
  };

  if (reportAsError) {
    logError(FLAGS_LOAD_TIMEOUT_MESSAGE, captureContext);
  } else {
    logWarning(FLAGS_LOAD_TIMEOUT_MESSAGE, captureContext);
  }
};

export const FeatureFlagProvider = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation(["general"]);
  const { session } = useAuth();
  const user = session?.user;
  const organisation = session?.organisation;
  const { features, setContext, clearContext } = useLaunchDarkly();
  const [flagsTimedOut, setFlagsTimedOut] = useState(false);
  // Replays sign in as a stand-in user whose flag request was never recorded, so
  // the mocked response is a fuzzy match that may not carry flagsAuthLoaded.
  const flagsWaitSkipped = isRunningInMeticulous();

  // Timeout to prevent infinite loading if LaunchDarkly fails
  useEffect(() => {
    if (!user || features.flagsAuthLoaded || flagsWaitSkipped) {
      return;
    }

    const timeout = setTimeout(() => {
      reportFlagsTimeout();
      setFlagsTimedOut(true);
    }, FLAGS_LOAD_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [user, features.flagsAuthLoaded, flagsWaitSkipped]);

  useEffect(() => {
    if (!user) {
      clearContext();
      // resets the flags-timeout state when the user signs out, alongside clearing the LaunchDarkly context; synchronizes to external auth state
      setFlagsTimedOut(false);
      return;
    }

    setContext("user", {
      key: user.id,
      email: user.email ?? undefined,
      name: user.name,
      avatar: user.photoUrl,
    });
  }, [user, setContext, clearContext]);

  useEffect(() => {
    if (!organisation) {
      setContext("organisation", {
        anonymous: true,
      });

      return;
    }
    setContext("organisation", {
      key: organisation.id,
      name: organisation.name,
    });
  }, [organisation, setContext]);

  const value = useMemo(() => ({ features }), [features]);
  const waitingForFlags =
    user && !features.flagsAuthLoaded && !flagsTimedOut && !flagsWaitSkipped;

  return (
    <FeatureFlagContext value={value}>
      {waitingForFlags ? (
        <GlobalLoading label={t("general:globalLoading.features")} />
      ) : (
        children
      )}
    </FeatureFlagContext>
  );
};

export const useFeatureFlags = () => {
  const ctxt = use(FeatureFlagContext);
  if (!ctxt) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagProvider",
    );
  }
  return ctxt;
};
